// src/components/Home/HeroSection.jsx - MOBILE OPTIMIZED
import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Loader, ChevronDown, Smartphone } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HeroSection = () => {
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [revealStage, setRevealStage] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [videoAspectRatio, setVideoAspectRatio] = useState(16/9);

  // Video file - optimized for web
  const backgroundVideo = `${process.env.PUBLIC_URL}/assets/videos/hero/hero-1.webm`;

  // Fallback image in case video fails
  const fallbackImage = '/assets/images/hero/hero-background.jpg';

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get video aspect ratio
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      const aspect = video.videoWidth / video.videoHeight;
      setVideoAspectRatio(aspect || 16/9);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => {
      setIsLoading(false);
      // Auto-play when ready with reduced volume
      video.volume = isMobile ? 0 : 0.3; // Mute on mobile by default
      video.play().catch(e => {
        console.log('Auto-play prevented:', e);
        setIsPlaying(false);
      });
    };

    const handleError = () => {
      console.error('Video failed to load');
      setVideoError(true);
      setIsLoading(false);
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // Preload video
    video.load();

    // GSAP Timeline for cinematic reveal
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onUpdate: () => {
          const progress = tl.progress();
          if (progress > 0.2) setRevealStage(1);
          if (progress > 0.4) setRevealStage(2);
          if (progress > 0.6) setRevealStage(3);
          if (progress > 0.8) setRevealStage(4);
        }
      });

      // Initial video fade-in (3 seconds)
      tl.fromTo('.video-overlay',
        { opacity: 1 },
        { opacity: 0, duration: 3, ease: 'power2.inOut' }
      );

      // Main title reveal - Mobile responsive adjustments
      const titleDuration = isMobile ? 0.8 : 1.2;
      
      tl.fromTo('.title-word-1',
        { y: isMobile ? 80 : 120, opacity: 0, scale: 0.8 },
        { y: 0, opacity: 1, scale: 1, duration: titleDuration },
        '+=0.5'
      );
      
      tl.fromTo('.title-word-2',
        { y: isMobile ? 80 : 120, opacity: 0, scale: 0.8 },
        { y: 0, opacity: 1, scale: 1, duration: titleDuration },
        `-=${isMobile ? 0.6 : 0.8}`
      );

      tl.fromTo('.title-word-3',
        { y: isMobile ? 80 : 120, opacity: 0, scale: 0.8 },
        { y: 0, opacity: 1, scale: 1, duration: titleDuration },
        `-=${isMobile ? 0.6 : 0.8}`
      );

      // Subtitle reveal
      tl.fromTo('.hero-divider',
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1, ease: 'back.out(1.7)' },
        '+=0.3'
      );

      tl.fromTo('.hero-subtitle',
        { y: isMobile ? 30 : 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 },
        '-=0.5'
      );

      // Description reveal
      tl.fromTo('.hero-description',
        { y: isMobile ? 20 : 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '+=0.2'
      );

      // CTA buttons reveal
      tl.fromTo('.cta-1',
        { scale: 0.7, opacity: 0, y: isMobile ? 15 : 20 },
        { scale: 1, opacity: 1, y: 0, duration: 0.6, ease: 'back.out(1.7)' },
        '+=0.2'
      );

      tl.fromTo('.cta-2',
        { scale: 0.7, opacity: 0, y: isMobile ? 15 : 20 },
        { scale: 1, opacity: 1, y: 0, duration: 0.6, ease: 'back.out(1.7)' },
        '-=0.3'
      );

      // Scroll indicator animation
      gsap.to('.scroll-indicator', {
        y: isMobile ? 8 : 10,
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });

    }, heroRef);

    // Handle scroll for desktop parallax only
    const handleScroll = () => {
      if (!isMobile && videoRef.current) {
        const scrolled = window.scrollY;
        if (scrolled < 500) {
          const parallax = scrolled * 0.5;
          videoRef.current.style.transform = `translateY(${parallax}px) scale(${1 + scrolled * 0.0005})`;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      window.removeEventListener('scroll', handleScroll);
      ctx.revert();
    };
  }, [isMobile]);

  const togglePlayPause = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      gsap.to(videoRef.current, { opacity: 0.7, duration: 0.3 });
    } else {
      videoRef.current.play().catch(e => console.log('Play error:', e));
      gsap.to(videoRef.current, { opacity: 1, duration: 0.3 });
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Calculate video container size for mobile
  const getVideoContainerStyle = () => {
    if (!isMobile) return {};
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const videoHeight = viewportWidth / videoAspectRatio;
    
    if (videoHeight > viewportHeight * 0.7) {
      // If video is too tall, scale to fit height
      const scale = (viewportHeight * 0.7) / videoHeight;
      return {
        width: `${viewportWidth * scale}px`,
        height: `${viewportHeight * 0.7}px`,
        margin: '0 auto'
      };
    }
    
    return {
      width: '100%',
      height: `${videoHeight}px`,
      margin: '0 auto'
    };
  };

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">

      {/* Video Background */}
      <div className="absolute inset-0 z-0 overflow-hidden bg-black">
        {!videoError ? (
          <>
            {/* Video Container - Different behavior for mobile */}
            <div 
              ref={videoContainerRef}
              className={`absolute ${
                isMobile 
                  ? 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' 
                  : 'inset-0'
              }`}
              style={isMobile ? getVideoContainerStyle() : {}}
            >
              <video
                ref={videoRef}
                className={`absolute ${
                  isMobile 
                    ? 'w-full h-full object-contain'  // Show full video on mobile
                    : 'inset-0 w-full h-full object-cover'  // Cover on desktop
                }`}
                muted={isMuted}
                loop
                playsInline
                preload="auto"
                poster={fallbackImage}
                aria-label="Background video showing sustainable protein production"
                style={{
                  filter: 'brightness(0.7) contrast(1.1)',
                  transform: isMobile ? 'scale(1)' : 'scale(1.05)',
                  transition: 'transform 0.3s ease-out'
                }}
              >
                <source src={backgroundVideo} type="video/mp4" />
                <source src={backgroundVideo.replace('.mp4', '.webm')} type="video/webm" />
                <img src={fallbackImage} alt="Sustainable Protein Production" />
              </video>
              
              {/* Initial loading overlay */}
              <div className="video-overlay absolute inset-0 bg-black" />
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="absolute inset-0 bg-black flex items-center justify-center">
                  <div className="text-center">
                    <div className="relative w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 md:mb-6">
                      <div className="absolute inset-0 border-3 md:border-4 border-gold-500/30 rounded-full"></div>
                      <div className="absolute inset-3 md:inset-4 border-3 md:border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gold-300 text-sm md:text-lg font-light tracking-wider">
                      {isMobile ? 'LOADING' : 'LOADING EXPERIENCE'}
                    </p>
                    <div className="w-32 md:w-48 h-1 bg-gold-500/30 rounded-full mt-3 md:mt-4 mx-auto overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-gold-500 to-gold-600 animate-shimmer"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          // Fallback image
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center animate-pulse-slow"
            style={{ backgroundImage: `url(${fallbackImage})` }}
          />
        )}
        
        {/* Gradient overlays - Different for mobile */}
        {isMobile ? (
          // Mobile: Simple gradient overlays
          <>
            <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black/90" />
            <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-black to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black to-transparent" />
          </>
        ) : (
          // Desktop: Full gradients
          <>
            <div className="absolute inset-0 bg-gradient-to-b from-dark-900/85 via-dark-900/50 to-dark-900/70" />
            <div className="absolute inset-0 bg-gradient-to-r from-dark-900/30 via-transparent to-dark-900/30" />
            <div className="absolute inset-0 shadow-[inset_0_0_200px_rgba(0,0,0,0.7)]" />
          </>
        )}
        
        {/* Glitter effect overlay - Reduced opacity on mobile */}
        <div className={`absolute inset-0 glitter-bg ${
          isMobile ? 'opacity-10' : 'opacity-20'
        }`} />
      </div>

      {/* Content Container */}
      <div className="relative z-10 container mx-auto px-4 md:px-6 text-center">
        <div className="max-w-5xl mx-auto">
          {/* Main Title */}
          <h1 className="mb-6 md:mb-12">
            <div className="overflow-hidden mb-1 md:mb-2">
              <span className="title-word-1 inline-block text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold gold-gradient font-playfair leading-tight tracking-tight">
                Powering the Future
              </span>
            </div>
            
            <div className="overflow-hidden mb-1 md:mb-2">
              <span className="title-word-2 inline-block text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold gold-gradient font-playfair leading-tight tracking-tight">
                of Protein Through
              </span>
            </div>
            
            <div className="overflow-hidden">
              <span className="title-word-3 inline-block text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white font-playfair leading-tight tracking-tight">
                Sustainable Innovation
              </span>
            </div>
          </h1>
          
          {/* Divider */}
          <div className="hero-divider opacity-0 transform origin-center mb-6 md:mb-8">
            <div className="w-24 md:w-32 h-0.5 md:h-1 bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600 mx-auto rounded-full" />
          </div>
          
          {/* Subtitle */}
          <div className="hero-subtitle opacity-0 mb-6 md:mb-8 px-2">
            <p className="text-lg sm:text-xl md:text-2xl text-gray-200 font-light leading-relaxed max-w-3xl mx-auto tracking-wide">
              The world is facing a structural shift in how protein is produced, 
              distributed, and consumed.
              <span className={`inline-block ml-1 md:ml-2 transition-all duration-500 ${
                revealStage >= 2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 md:translate-x-4'
              }`}>
                We are building that system.
              </span>
            </p>
          </div>

          {/* Description */}
          {/* <div className="hero-description opacity-0 mb-8 md:mb-12 max-w-2xl mx-auto px-2">
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed tracking-wide">
              <span className={`inline-block transition-all duration-500 delay-100 ${
                revealStage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 md:translate-y-4'
              }`}>
                Rising population, climate pressure, and resource constraints demand 
                new protein systems
              </span>
              <span className={`inline-block ml-1 transition-all duration-500 delay-300 ${
                revealStage >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 md:translate-y-4'
              }`}>
                — not incremental improvements to old ones.
              </span>
            </p>
          </div> */}

          {/* CTA Buttons - Stack on mobile */}
          {/* <div className="hero-cta flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center px-2">
            <button className={`cta-1 opacity-0 px-6 py-3 md:px-8 md:py-4 bg-gradient-to-r from-gold-600 to-gold-700 text-dark-900 font-bold text-base md:text-lg rounded-full hover:from-gold-500 hover:to-gold-600 transition-all duration-300 transform hover:scale-105 shadow-xl md:shadow-2xl hover:shadow-gold-500/30 group relative overflow-hidden w-full sm:w-auto`}>
              <span className="relative z-10">Partner With Us</span>
              <div className="absolute inset-0 bg-gradient-to-r from-gold-500 to-gold-600 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
            </button>
            
            <button className={`cta-2 opacity-0 px-6 py-3 md:px-8 md:py-4 border-2 border-gold-500 text-gold-300 font-bold text-base md:text-lg rounded-full hover:bg-gold-500/10 transition-all duration-300 transform hover:scale-105 group relative overflow-hidden w-full sm:w-auto`}>
              <span className="relative z-10">Request Investor Overview</span>
              <div className="absolute inset-0 bg-gold-500/5 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
            </button>
          </div> */}

          {/* Progress indicator - Hidden on mobile */}
          {!isMobile && (
            <div className="mt-12 md:mt-16 flex justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((stage) => (
                <div
                  key={stage}
                  className={`w-2 h-2 rounded-full transition-all duration-500 ${
                    revealStage >= stage 
                      ? 'bg-gold-500 w-8' 
                      : 'bg-gold-500/30'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Video Controls - Mobile optimized */}
      {/* <div className={`absolute z-20 ${
        isMobile 
          ? 'bottom-24 right-4 flex-col space-y-3'  // Higher up on mobile
          : 'bottom-8 right-8 flex-row space-x-3'   // Bottom right on desktop
      } flex items-center`}>
        <button
          onClick={toggleMute}
          className={`p-2 rounded-full bg-black/60 backdrop-blur-md border border-gold-500/20 hover:bg-gold-500/20 transition-all duration-300 ${
            isMobile ? 'hover:scale-110' : 'hover:scale-110'
          } group`}
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? (
            <VolumeX className="w-4 h-4 md:w-5 md:h-5 text-gold-400 group-hover:text-gold-300" />
          ) : (
            <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-gold-400 group-hover:text-gold-300" />
          )}
        </button>
        
        <button
          onClick={togglePlayPause}
          className={`p-2 rounded-full bg-black/60 backdrop-blur-md border border-gold-500/20 hover:bg-gold-500/20 transition-all duration-300 ${
            isMobile ? 'hover:scale-110' : 'hover:scale-110'
          } group`}
          aria-label={isPlaying ? "Pause video" : "Play video"}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4 md:w-5 md:h-5 text-gold-400 group-hover:text-gold-300" />
          ) : (
            <Play className="w-4 h-4 md:w-5 md:h-5 text-gold-400 group-hover:text-gold-300" />
          )}
        </button>
      </div> */}

      {/* Scroll Indicator - Smaller on mobile */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="scroll-indicator flex flex-col items-center">
          <ChevronDown className={`${
            isMobile ? 'w-5 h-5' : 'w-6 h-6'
          } text-gold-400/70 animate-pulse mb-1 md:mb-2`} />
          <div className={`${
            isMobile ? 'text-xs' : 'text-xs'
          } text-gold-400/50 tracking-widest uppercase hidden md:block`}>
            Scroll to Explore
          </div>
        </div>
      </div>

      {/* Mobile Video Info */}
      {/* {isMobile && !isLoading && (
        <div className="absolute bottom-4 left-4 z-20">
          <div className="text-xs text-gold-400/50 bg-black/50 backdrop-blur-sm px-2 py-1 rounded">
            Video playing • Tap to control
          </div>
        </div>
      )} */}
    </section>
  );
};

export default HeroSection;